import { useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Box, Text } from "rebass/styled-components";

import { HERO, HERO_BACKGROUND } from "../data";
import {
  ACCENT,
  Cmd,
  CommandMark,
  Section,
  SectionHeading,
} from "../../styledComponents";

const PROCESSING_MS = 3800;

export const Hero = () => {
  const [processing, setProcessing] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // The demo ends on the !allstar being typed. Hold on a "processing" beat so
  // the loop shows the payoff, then replay.
  const handleEnded = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      videoRef.current?.play();
    }, PROCESSING_MS);
  };

  return (
    <HeroShell>
      <Section pt={[64, 88, 112]} pb={[64, 88, 112]}>
        <Split>
          <Pitch>
            <SectionHeading>
              {HERO.headingLead}
              <HeroCommand>{HERO.headingCommand}</HeroCommand>
            </SectionHeading>

            <Text
              color="chalk"
              fontWeight={500}
              fontSize={[18, 20, 20, 22]}
              lineHeight={1.6}
              maxWidth="46ch"
              mt={5}
            >
              Type <Cmd>!allstar</Cmd> in all chat after you do something worth
              keeping. If we can reach your match, we make the clip for free,
              even if you have hit your clip limit.
            </Text>
          </Pitch>

          <Media>
            <VideoFrame>
              <video
                ref={videoRef}
                src={HERO.video}
                autoPlay
                muted
                playsInline
                onEnded={handleEnded}
              />
              {processing && (
                <Processing>
                  <Spinner aria-hidden="true" />
                  <ProcessingText>
                    <small>
                      <Cmd>!allstar</Cmd> detected
                    </small>
                    Processing <span>{HERO.processingTitle}</span>
                  </ProcessingText>
                  <ProcessingBar />
                </Processing>
              )}
            </VideoFrame>
            <Caption>{HERO.caption}</Caption>
          </Media>
        </Split>
      </Section>
    </HeroShell>
  );
};

const SPLIT_STACK_BREAK = "905px";

const Split = styled.div`
  align-items: center;
  display: flex;
  gap: 56px;
  width: 100%;

  @media (max-width: ${SPLIT_STACK_BREAK}) {
    flex-direction: column;
    gap: 40px;
  }
`;

const Pitch = styled.div`
  align-items: flex-start;
  display: flex;
  flex: 0 0 40%;
  flex-direction: column;
  min-width: 0;

  @media (max-width: ${SPLIT_STACK_BREAK}) {
    align-items: center;
    flex: 1 1 auto;
    text-align: center;
  }
`;

const Media = styled(Box)`
  flex: 1;
  min-width: 0;

  @media (max-width: ${SPLIT_STACK_BREAK}) {
    flex: 1 1 auto;
    width: 100%;
  }
`;

// The command owns the second line of the headline: it is the whole point of
// the page, so it never shares a line with the setup copy at any width.
const HeroCommand = styled(CommandMark)`
  display: block;
`;

// A real map render sits behind the hero, dimmed so it reads as atmosphere
// rather than a picture competing with the headline. The scrim ends on the page
// background so the band fuses into the section below it.
const HeroShell = styled(Box)`
  background: ${({ theme }) => theme.colors.navy900};
  overflow: hidden;
  position: relative;

  ::before {
    background-image: url("${HERO_BACKGROUND}");
    background-position: center 35%;
    background-size: cover;
    content: "";
    inset: 0;
    opacity: 0.26;
    position: absolute;
  }

  ::after {
    background: radial-gradient(
        900px 420px at 68% 8%,
        rgba(77, 189, 148, 0.18),
        transparent 70%
      ),
      linear-gradient(
        180deg,
        rgba(11, 17, 22, 0.58) 0%,
        rgba(11, 17, 22, 0.72) 55%,
        ${({ theme }) => theme.colors.darkerNed} 100%
      );
    content: "";
    inset: 0;
    pointer-events: none;
    position: absolute;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

// aspect-ratio lives on the wrapper, not the <video>: a replaced element has no
// height before metadata loads, which would shift the layout on first paint.
const VideoFrame = styled(Box)`
  aspect-ratio: 16 / 9;
  border-radius: 18px;
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.55);
  overflow: hidden;
  position: relative;
  width: 100%;

  video {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`;

const Caption = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13.5px;
  margin-top: 12px;
  opacity: 0.8;
  text-align: center;
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fillBar = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const Processing = styled.div`
  align-items: center;
  animation: ${fadeIn} 0.2s ease;
  backdrop-filter: blur(3px);
  background: rgba(11, 17, 22, 0.82);
  display: flex;
  flex-direction: column;
  gap: 16px;
  inset: 0;
  justify-content: center;
  padding: 24px;
  position: absolute;
  text-align: center;
  z-index: 2;
`;

const Spinner = styled.div`
  animation: ${spin} 0.8s linear infinite;
  border: 3px solid rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  border-top-color: ${ACCENT};
  height: 34px;
  width: 34px;
`;

const ProcessingText = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 15px;
  font-weight: 700;

  small {
    color: ${({ theme }) => theme.colors.chalk};
    display: block;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    margin-bottom: 7px;
  }

  span {
    color: ${ACCENT};
  }
`;

const ProcessingBar = styled.div`
  background: rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  height: 5px;
  max-width: 240px;
  overflow: hidden;
  width: 70%;

  &::after {
    animation: ${fillBar} ${PROCESSING_MS}ms ease forwards;
    background: ${ACCENT};
    border-radius: 999px;
    content: "";
    display: block;
    height: 100%;
  }
`;
